import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import { AppStoreModule } from '@/store';
import { TenantDetailsApi, MicrosoftPartner } from '@/common/models';
import IAdminService from '@/services/http/interfaces/IAdminService';
import { TenantDetailsForm } from '@/common/viewModels/tenantDetails';
import { TenantDetailsFormStateTenant } from '@/common/viewModels/tenantDetails/form/state/TenantDetailsFormStateTenant';
import { TenantDetailsFormStateMicrosoftPartner } from '@/common/viewModels/tenantDetails/form/state/TenantDetailsFormStateMicrosoftPartner';
import { TenantsHelper } from '@/common/helpers';
import { CloudOrganizationStatus } from '@/common/viewModels/Enums';
import { TenantPartner } from '@/common/models/TenantPartner';
import { ItemViewModel } from '@/common/viewModels';
import { FormLookupsBase } from '@/common/form/FormLookupsBase';

export interface ITenantDetailsState {
  tenantDetails: TenantDetailsApi | null;
  form: TenantDetailsForm;
}

@Module({
  name: 'tenantDetails',
  namespaced: true,
})
export default class TenantDetailsStore extends VuexModule implements ITenantDetailsState {

  // State
  tenantDetails: TenantDetailsApi | null = null;
  tenantPartners: ItemViewModel[] | null = null;
  form: TenantDetailsForm = new TenantDetailsForm();
  salesChannels: ItemViewModel[] = [
    { text: 'Telstra', value: 'false', disabled: false },
    { text: 'Partner', value: 'true', disabled: false },
  ];


  // Getters
  get isFormValid(): boolean {
    const isTenantFormValid: boolean = this.form.state.tenant.isValid;
    const isMicrosoftPartnerFormValid: boolean = this.form.state.microsoftPartner.isValid;
    return isTenantFormValid && isMicrosoftPartnerFormValid;
  }

  get isMicrosoftPartnerFormValid(): boolean {
    return this.form.state.microsoftPartner.isValid;
  }

  // Mutations
  @Mutation
  LOAD_TENANT_FORM(tenantDetails: TenantDetailsApi) {
    Object.assign(this.form.data.tenant, {
      name: tenantDetails.name,
      cloudHealthClientApiId: tenantDetails.cloudHealthClientApiId,
      odinAccountId: tenantDetails.odinAccountId,
      cidn: tenantDetails.cidn,
      odinPayToolId: tenantDetails.odinPayToolId,
      isPartnerSalesChannel: tenantDetails.isPartnerSalesChannel,
      accountManagerName: tenantDetails.accountManagerName,
      partnerDealerCode: tenantDetails.partnerDealerCode ? tenantDetails.partnerDealerCode : '',
      awsDirectMasterAccountId: tenantDetails.awsDirectMasterAccountId,
      cloudOrganizationId: tenantDetails.cloudOrganizationId,
      authenticationDomain: tenantDetails.authenticationDomain,
      dome9AccountId: tenantDetails.dome9AccountId ? tenantDetails.dome9AccountId : '',
      azurePlanOdinSubscriptionId: tenantDetails.azurePlanOdinSubscriptionId ? tenantDetails.azurePlanOdinSubscriptionId : '',
    });

    Object.assign(this.form.data.microsoftPartner, {
      mpnId: tenantDetails.mpnId || '',
      mpnName: tenantDetails.mpnName,
    });

    this.form.state.tenant = new TenantDetailsFormStateTenant();
    this.form.state.microsoftPartner = new TenantDetailsFormStateMicrosoftPartner();
  }

  @Mutation
  SET_FORM_DO_VALIDATION(doValidation: boolean) {
    this.form.doValidation = doValidation;
  }

  @Mutation
  UPDATE_TENANT_FORM_FIELD_BY_NAME(payload: { fieldName: string, value: any }) {
    if (payload.fieldName === 'isPartnerSalesChannel' && payload.value === 'true') {
      (this.form.data.tenant as any).accountManagerName = null;
      (this.form.data.tenant as any).isPartnerSalesChannel = true;
    } else if (payload.fieldName === 'isPartnerSalesChannel' && payload.value === 'false') {
      (this.form.data.tenant as any).partnerDealerCode = null;
      (this.form.data.tenant as any).isPartnerSalesChannel = false;
    } else {
      (this.form.data.tenant as any)[payload.fieldName] = payload.value;
    }
  }

  @Mutation
  async UPDATE_TENANT_FORM_STATE() {
    this.form.state.tenant.validate(this.form.doValidation, this.form.data.tenant);
  }

  @Mutation
  RESET_MICROSOFT_PARTNER_FORM_STATE() {
    this.form.state.microsoftPartner.resetState();
  }

  @Mutation
  async UPDATE_MICROSOFT_PARTNER_FORM_LOADING(loading: boolean) {
    this.form.data.microsoftPartner.loading = loading;
  }

  @Mutation
  SET_TENANT_DETAILS(tenantDetails: TenantDetailsApi) {
    this.tenantDetails = tenantDetails;
  }

  @Mutation
  SET_TENANT_PARTNERS(tenantPartners: TenantPartner[]) {
    const sortedTenantPartners: TenantPartner[] = tenantPartners.sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0);
    this.tenantPartners = FormLookupsBase.populate(sortedTenantPartners, 'name', 'partnerDealerCode');
  }

  @Mutation
  SET_TENANT_DETAILS_CLOUD_ORGANIZATION_STATUS_CREATED() {
    this.tenantDetails!.cloudOrganizationStatus = CloudOrganizationStatus.Created;
  }

  @Mutation
  SET_MICROSOFT_PARTNER_INPUT(mpnId: string) {
    Object.assign(this.form.data.microsoftPartner, {
      mpnId,
      mpnName: '',
      isMpnValid: false,
      isMpnValidating: false,
    });
  }

  @Mutation
  RESET_MICROSOFT_PARTNER_FORM_DATA(clearMpnId: boolean = false) {
    if (clearMpnId) {
      this.form.data.microsoftPartner.mpnId = '';
    }

    Object.assign(this.form.data.microsoftPartner, {
      mpnName: '',
      isMpnValid: false,
      isMpnValidating: false,
      isMpnValidated: false,
    });
    this.tenantDetails!.mpnId = '';
  }

  @Mutation
  SET_MICROSOFT_PARTNER_VALIDATED_SUCCESS(partnersMpn: MicrosoftPartner) {
    Object.assign(this.form.data.microsoftPartner, {
      mpnId: partnersMpn.mpnId,
      mpnName: partnersMpn.name,
      loading: false,
      isMpnValid: true,
      isMpnValidating: false,
      isMpnValidated: true,
    });

    this.tenantDetails!.mpnId = partnersMpn.mpnId;
  }

  @Mutation
  SET_MICROSOFT_PARTNER_VALIDATED_ERROR() {
    Object.assign(this.form.data.microsoftPartner, {
      loading: false,
      isMpnValid: false,
      isMpnValidating: false,
      isMpnValidated: true,
    });
  }

  @Mutation
  SET_MICROSOFT_PARTNER_BEFORE_SERVER_VALIDATION() {
    this.form.data.microsoftPartner.isMpnValidating = true;
    this.form.data.microsoftPartner.isMpnValidated = false;
  }

  @Mutation
  async VALIDATE_MICROSOFT_PARTNER_FORM() {
    await this.form.state.microsoftPartner.validate(true, this.form.data.microsoftPartner);
  }

  // Actions
  @Action
  async getTenantDetails(payload: { adminService: IAdminService, tenantId: string }) {

    AppStoreModule.SET_ISLOADING(true);

    try {
      const tenantDetails: TenantDetailsApi = await payload.adminService.getTenantDetails(payload.tenantId);
      this.SET_TENANT_DETAILS(tenantDetails);
      this.LOAD_TENANT_FORM(tenantDetails);
    } catch (error) {
      AppStoreModule.SAVE_HTTP_ERROR('Unable to load tenant');
    }

    AppStoreModule.SET_ISLOADING(false);

  }

  @Action
  async getTenantPartners(payload: { adminService: IAdminService, tenantId: string }) {

    AppStoreModule.SET_ISLOADING(true);

    try {
      const tenantPartners: TenantPartner[] = await payload.adminService.getTenantPartners(payload.tenantId);
      this.SET_TENANT_PARTNERS(tenantPartners);
    } catch (error) {
      AppStoreModule.SAVE_HTTP_ERROR('Unable to load tenant partners');
    }

    AppStoreModule.SET_ISLOADING(false);

  }

  @Action
  async tenantFormInputChange(payload: { fieldName: string, value: any }) {
    this.UPDATE_TENANT_FORM_FIELD_BY_NAME(payload);
    await this.UPDATE_TENANT_FORM_STATE();
  }

  @Action
  microsoftPartnerInput(mpnId: string) {
    this.SET_MICROSOFT_PARTNER_INPUT(mpnId);
    this.RESET_MICROSOFT_PARTNER_FORM_STATE();
  }

  @Action
  editMicrosoftPartner() {
    this.RESET_MICROSOFT_PARTNER_FORM_DATA();
    this.RESET_MICROSOFT_PARTNER_FORM_STATE();
  }

  @Action
  removeMicrosoftPartner() {
    this.RESET_MICROSOFT_PARTNER_FORM_DATA(true);
    this.RESET_MICROSOFT_PARTNER_FORM_STATE();
  }

  @Action
  setFormDoValidation(doValidation: boolean) {
    this.SET_FORM_DO_VALIDATION(doValidation);
  }

  @Action({ rawError: true })
  async requestEdit(adminService: IAdminService): Promise<boolean> {
    let isSuccessful: boolean = false;

    if (!this.form.doValidation) {
      this.SET_FORM_DO_VALIDATION(true);
    }

    await Promise.all([
      this.UPDATE_TENANT_FORM_STATE(),
      this.VALIDATE_MICROSOFT_PARTNER_FORM(),
    ]);

    if (this.isFormValid) {
      AppStoreModule.SET_ISLOADING(true);

      try {
        const tenantDetailsApi: TenantDetailsApi = TenantsHelper.getUpdateTenantDetails(this.tenantDetails!, this.form.data.tenant, this.form.data.microsoftPartner);
        await adminService.updateTenantDetails(this.tenantDetails!.tenantId, tenantDetailsApi);
        isSuccessful = true;
      } catch (error) {
        AppStoreModule.SAVE_HTTP_ERROR('Unable to update tenant');
      } finally {
        AppStoreModule.SET_ISLOADING(false);
      }
    }
    return isSuccessful;
  }

  @Action
  async activateCloudOrganization(adminService: IAdminService): Promise<void> {
    AppStoreModule.SET_ISLOADING(true);

    try {
      await adminService.activateCloudOrganization(this.tenantDetails!.tenantId);
    } catch (error) {
      AppStoreModule.SAVE_HTTP_ERROR('Unable to set the tenant to active');
    } finally {
      AppStoreModule.SET_ISLOADING(false);
    }

  }

  @Action
  async verifyMicrosoftPartnerDetails(adminService: IAdminService): Promise<void> {
    this.SET_MICROSOFT_PARTNER_BEFORE_SERVER_VALIDATION();
    // Promise.all used to force digest cycle. When await was used the functionality only worked every second button click
    await Promise.all([this.VALIDATE_MICROSOFT_PARTNER_FORM()]);

    try {
      if (this.isMicrosoftPartnerFormValid) {

        this.RESET_MICROSOFT_PARTNER_FORM_STATE();
        this.UPDATE_MICROSOFT_PARTNER_FORM_LOADING(true);

        adminService.getMicrosoftPartnerDetails(this.tenantDetails!.tenantId, this.form.data.microsoftPartner.mpnId!)
          .then(async (microsoftPartner: MicrosoftPartner) => {
            this.SET_MICROSOFT_PARTNER_VALIDATED_SUCCESS(microsoftPartner);
          }, async () => {
            this.SET_MICROSOFT_PARTNER_VALIDATED_ERROR();
          }).finally(async () => {
            await this.VALIDATE_MICROSOFT_PARTNER_FORM();
          });

      }
    } catch (error) {
      this.SET_MICROSOFT_PARTNER_VALIDATED_ERROR();
      await this.VALIDATE_MICROSOFT_PARTNER_FORM();
    }

  }
}
